import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import backArrow from "../../assets/back-arrow.png";
import {
  submitFormAnswers,
  getFormAnswers,
  completeForm,
  getApplication,
  getApplicationForms,
  getFormReadiness,
  type FormReadiness,
  type FormReadinessMissingItem,
} from "../../API/applicationAPI";
import { toast } from "react-toastify";
import { getUserActiveFeedbackBatch } from "../../API/feedbackAPI";
import type {
  FeedbackBatchDetail,
  FeedbackItemDetail,
} from "../../API/feedbackAPI";
import { dispatchFormScrollToQuestion } from "./formQuestionScroll";
import FormSidebar from "../../components/FormSidebar";
import { useMultipleAnswerForm } from "./hooks/useMultipleAnswerForm";
import { useFormData } from "./hooks/useFormData";
import { decodeFormTitleParam } from "./utils/formUtils";
import { FormContent, FormDrawer } from "./components";
import SubmitConfirmationModal, {
  type SubmitConfirmationState,
} from "./components/SubmitConfirmationModal";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useConfirmDialog } from "../../components/useConfirmDialog";
import { findMissingInCategory } from "../../utils/categoryValidation";
import { findFormatErrorsInCategory } from "../../utils/categoryFormatValidation";
import {
  applyInitializeEntryCounts,
  buildInitializeEntryCountEffectKey,
} from "../../utils/initializeEntryCount";
import {
  reconcileCountDrivenEntries,
  clearHiddenCountDrivenAnswers,
} from "../../utils/countDrivenEntries";
import { visibleQuestionIds } from "../../utils/formVisibility";
import { getVisibleLeafCategories } from "../../utils/categoryVisibility";
import {
  buildMissingCategoriesTree,
  type MissingCategoryNode,
} from "../../utils/missingCategoriesTree";
import type { DrawerTab } from "./components/FormDrawer";
import { Category, QuestionItem } from "../../types/form";
import type { ApplicationStage } from "../../types/application";

type PrefillMapping = {
  formId?: number;
  formNumber?: string;
  questionId: number;
  sourceField?: string;
  targetField?: string;
  onlyWhenEmpty?: boolean;
};

/** No edits (except reviewer notes flow uses separate partial lock via revision_requested). */
const FORM_FULLY_VIEW_ONLY_STAGES = new Set<ApplicationStage>([
  "under_review",
  "ready_for_uscis",
  "uscis_form_generated",
  "signature_pending",
  "signature_completed",
  "submitted_to_uscis",
  "approved",
  "rejected",
  "closed",
]);

function collectQuestionIdsInCategory(category: Category): Set<number> {
  const ids = new Set<number>();
  const visit = (cat: Category) => {
    cat.questions?.forEach((q) => ids.add(q.id));
    cat.subcategories?.forEach(visit);
  };
  visit(category);
  return ids;
}

function collectQuestionsInCategories(categories: Category[] | undefined): QuestionItem[] {
  const out: QuestionItem[] = [];
  const visit = (category: Category) => {
    category.questions?.forEach((q) => out.push(q));
    category.subcategories?.forEach(visit);
  };
  categories?.forEach(visit);
  return out;
}

function getQuestionInputName(question: QuestionItem): string {
  return question.name || `q_${question.id}`;
}

function isPrefillEmpty(value: any): boolean {
  if (value === undefined || value === null || value === "") return true;
  if (Array.isArray(value)) return value.length === 0 || value.every(isPrefillEmpty);
  if (typeof value === "object") {
    return Object.values(value).every(isPrefillEmpty);
  }
  return false;
}

function getSavedAnswerByQuestionId(
  answers: Record<string, any>,
  questionId: number,
): any {
  return (
    answers[questionId] ??
    answers[String(questionId)] ??
    answers[`q_${questionId}`]
  );
}

function getSourceAnswerValue(
  sourceAnswers: Record<string, any>,
  mapping: PrefillMapping,
): any {
  const answer = getSavedAnswerByQuestionId(sourceAnswers, mapping.questionId);
  if (!mapping.sourceField) return answer;

  const record = Array.isArray(answer)
    ? answer.find((entry) => entry && typeof entry === "object")
    : answer;
  if (!record || typeof record !== "object") return undefined;
  return record[mapping.sourceField];
}

function sourceKey(mapping: PrefillMapping, fallbackFormId?: number): string {
  if (mapping.formId != null) return `id:${mapping.formId}`;
  if (mapping.formNumber) return `number:${mapping.formNumber}`;
  return `id:${fallbackFormId ?? ""}`;
}

function applyPrefillMappings(
  answers: Record<string, any>,
  questions: QuestionItem[],
  sourceAnswersByKey: Map<string, Record<string, any>>,
): void {
  questions.forEach((question) => {
    const mappings = question.validationRules?.prefillFrom;
    if (!Array.isArray(mappings) || mappings.length === 0) return;

    const inputName = getQuestionInputName(question);
    const idKey = String(question.id);

    mappings.forEach((mapping) => {
      const sourceAnswers = sourceAnswersByKey.get(sourceKey(mapping));
      if (!sourceAnswers) return;

      const sourceValue = getSourceAnswerValue(sourceAnswers, mapping);
      if (isPrefillEmpty(sourceValue)) return;

      if (mapping.targetField) {
        const existing = answers[inputName] ?? answers[idKey];
        const current =
          existing && typeof existing === "object" && !Array.isArray(existing)
            ? existing
            : {};
        if (mapping.onlyWhenEmpty !== false && !isPrefillEmpty(current[mapping.targetField])) {
          return;
        }

        const nextValue = { ...current, [mapping.targetField]: sourceValue };
        answers[inputName] = nextValue;
        answers[idKey] = nextValue;
        return;
      }

      if (mapping.onlyWhenEmpty !== false && !isPrefillEmpty(answers[inputName] ?? answers[idKey])) {
        return;
      }
      answers[inputName] = sourceValue;
      answers[idKey] = sourceValue;
    });
  });
}

function parsePositiveIntParam(raw: string | null): number | undefined {
  if (raw == null || raw === "") return undefined;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function normaliseForDirtyCompare(value: any): any {
  if (Array.isArray(value)) {
    return value.map(normaliseForDirtyCompare);
  }
  if (value && typeof value === "object") {
    return Object.keys(value)
      .sort()
      .reduce<Record<string, any>>((acc, key) => {
        acc[key] = normaliseForDirtyCompare(value[key]);
        return acc;
      }, {});
  }
  return value;
}

function answersEqual(
  a: Record<string, any>,
  b: Record<string, any>
): boolean {
  return (
    JSON.stringify(normaliseForDirtyCompare(a)) ===
    JSON.stringify(normaliseForDirtyCompare(b))
  );
}

/** Highlight a feedback card when opening from email / deep link */
function pickDefaultFeedbackItemId(
  batch: FeedbackBatchDetail | null,
  activeCategory: Category | null,
  searchParams: URLSearchParams
): number | null {
  if (!batch?.items?.length) return null;
  const items = [...batch.items].sort((a, b) => a.orderIndex - b.orderIndex);

  const questionIdParam = parsePositiveIntParam(searchParams.get("feedbackQuestionId"));
  if (questionIdParam != null) {
    const byQ = items.find((i) => i.questionId === questionIdParam);
    if (byQ) return byQ.id;
  }

  if (activeCategory) {
    const inSection = collectQuestionIdsInCategory(activeCategory);
    const inCat = items.filter(
      (i) => i.questionId != null && inSection.has(i.questionId)
    );
    const pick = inCat.find((i) => i.status === "pending") ?? inCat[0];
    if (pick) return pick.id;
  }

  const firstPending = items.find((i) => i.status === "pending");
  return (firstPending ?? items[0]).id;
}

const StepperForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const formIdParam = searchParams.get("formId");
  const formId = formIdParam ? parseInt(formIdParam) : undefined;
  const applicationIdParam = searchParams.get("applicationId");
  const applicationId = applicationIdParam
    ? parseInt(applicationIdParam)
    : undefined;
  const formTitleParam = searchParams.get("formTitle");

  const [formTitle, setFormTitle] = useState<string>(
    decodeFormTitleParam(formTitleParam) ?? "Form",
  );

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [readiness, setReadiness] = useState<FormReadiness | null>(null);
  const [isCheckingReadiness, setIsCheckingReadiness] = useState<boolean>(false);
  // Submit confirmation modal — opened when the user hits Save on the
  // last section. `state` flips based on whether the freshly-saved form
  // passes the readiness gate.
  const [submitModalOpen, setSubmitModalOpen] = useState<boolean>(false);
  const [submitModalState, setSubmitModalState] =
    useState<SubmitConfirmationState>("confirm");
  const [submitModalMissingCategories, setSubmitModalMissingCategories] =
    useState<MissingCategoryNode[]>([]);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [drawerTab, setDrawerTab] = useState<DrawerTab>("hints");
  const [activeHint, setActiveHint] = useState<{
    title?: string;
    content: string;
  } | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [feedbackBatch, setFeedbackBatch] = useState<FeedbackBatchDetail | null>(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [selectedFeedbackItemId, setSelectedFeedbackItemId] = useState<number | null>(null);
  const [activeFeedbackItem, setActiveFeedbackItem] = useState<FeedbackItemDetail | null>(null);
  const [applicationStage, setApplicationStage] = useState<ApplicationStage | null>(null);
  const navigate = useNavigate();
  const { confirm, dialogProps: confirmDialogProps } = useConfirmDialog();

  const questionIdsWithFeedback = useMemo(() => {
    const s = new Set<number>();
    feedbackBatch?.items?.forEach((i) => {
      if (i.questionId != null) s.add(i.questionId);
    });
    return s;
  }, [feedbackBatch]);

  /** While batch has pending notes tied to questions, only those fields stay editable. */
  const questionIdsWithPendingFeedback = useMemo(() => {
    const s = new Set<number>();
    feedbackBatch?.items?.forEach((i) => {
      if (i.status === "pending" && i.questionId != null) s.add(i.questionId);
    });
    return s;
  }, [feedbackBatch]);

  // Keep form locked whenever a batch exists — even after all items resolved, until the user resubmits.
  const restrictEditsToFeedbackFields = (feedbackBatch?.items?.length ?? 0) > 0;

  /**
   * Marking an individual form complete no longer submits the package for
   * review. Forms stay editable until the application-level review submission
   * moves the case into a post-review stage.
   */
  const isFormViewOnly =
    applicationStage != null && FORM_FULLY_VIEW_ONLY_STAGES.has(applicationStage);

  const hasActionRequiredFeedback = useMemo(
    () => !!feedbackBatch?.items?.some((i) => i.status === "pending"),
    [feedbackBatch]
  );

  /** Hide Feedback drawer tab until we know there is reviewer content for this form. */
  const showFeedbackTab = useMemo(() => {
    if (feedbackLoading) return false;
    return (feedbackBatch?.items?.length ?? 0) > 0;
  }, [feedbackBatch, feedbackLoading]);

  const unresolvedCount = useMemo(
    () => feedbackBatch?.items?.filter((i) => i.status === "pending").length ?? 0,
    [feedbackBatch]
  );

  // useBlocker is not supported in BrowserRouter (CRA); use beforeunload to warn on tab close / refresh.
  const shouldBlock =
    unresolvedCount > 0 && feedbackBatch?.status === "submitted";

  useEffect(() => {
    if (!shouldBlock) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [shouldBlock]);

  // Use custom hooks for form data management
  const {
    isLoading,
    loadError,
    formStructure,
    activeCategory,
    expandedCategories,
    handleToggleExpand,
    handleSelectCategory,
    selectCategoryById,
    navigateToCategoryContainingQuestion,
  } = useFormData(formId);

  const openFeedbackInitialPickDoneRef = useRef(false);
  const postHydrationCategorySyncDoneRef = useRef(false);
  const lastSavedFormDataRef = useRef<Record<string, any>>({});
  const [savedAnswersHydrated, setSavedAnswersHydrated] = useState(false);

  useEffect(() => {
    openFeedbackInitialPickDoneRef.current = false;
    postHydrationCategorySyncDoneRef.current = false;
    lastSavedFormDataRef.current = {};
    setSavedAnswersHydrated(false);
  }, [applicationId, formId]);

  // The form's questions are derived further down, after this hook; the
  // reconcile step reads them through a ref so it always sees the latest.
  const formQuestionsRef = useRef<QuestionItem[]>([]);

  const {
    formData,
    setFormData,
    setFormDataFromEdit,
    handleChange,
    handleMultipleEntryChange,
    handleAddEntry,
    handleRemoveEntry,
    handleCompositeFieldChange,
    handleMultipleCompositeChange,
    formatForSubmission,
  } = useMultipleAnswerForm((prev, next) =>
    // Client worklist #82/#83: keep "how many children" and the children's
    // entries in step on the applicant's own edits.
    reconcileCountDrivenEntries(prev, next, formQuestionsRef.current),
  );

  // Title Updates
  useEffect(() => {
    const decoded = decodeFormTitleParam(formTitleParam);
    if (decoded && decoded !== formTitle) {
      setFormTitle(decoded);
    }
  }, [formTitleParam, formTitle]);

  useEffect(() => {
    if (!formStructure) return;
    if (formTitleParam) return;

    const derivedTitle = formStructure.form?.title || "Form";
    if (derivedTitle && derivedTitle !== formTitle) {
      setFormTitle(derivedTitle);
    }
  }, [formStructure, formTitleParam, formTitle]);

  // Clear validation errors when active category changes
  useEffect(() => {
    setValidationErrors({});
  }, [activeCategory?.id]);

  // Reset active hint when category changes so section hints show again
  useEffect(() => {
    setActiveHint(null);
  }, [activeCategory?.id]);

  const openHintsWithHint = (
    hint: { title?: string; content: string } | null,
  ) => {
    setActiveHint(hint);
    setDrawerTab("hints");
    setDrawerOpen(true);
  };

  const ensureHintsDrawerOpen = useCallback(() => {
    setDrawerTab("hints");
    setDrawerOpen(true);
  }, []);

  const scrollFormToFeedbackQuestion = useCallback(
    (questionId: number) => {
      if (questionId <= 0) return;
      navigateToCategoryContainingQuestion(questionId);
      dispatchFormScrollToQuestion({ questionId, delayMs: 420 });
    },
    [navigateToCategoryContainingQuestion]
  );

  const handleFeedbackNavigateToSource = useCallback(
    (item: FeedbackItemDetail) => {
      setSelectedFeedbackItemId(item.id);
      if (item.questionId != null && item.questionId > 0) {
        scrollFormToFeedbackQuestion(item.questionId);
      }
    },
    [scrollFormToFeedbackQuestion]
  );

  const openFeedbackWithQuestion = (questionId: number) => {
    const item = feedbackBatch?.items?.find((i) => i.questionId === questionId);
    if (item) {
      setSelectedFeedbackItemId(item.id);
    }
    const hasItems = (feedbackBatch?.items?.length ?? 0) > 0;
    setDrawerTab(hasItems ? "feedback" : "hints");
    setDrawerOpen(true);
    scrollFormToFeedbackQuestion(questionId);
  };

  const handleFeedbackBatchUpdated = useCallback(
    async (batch: FeedbackBatchDetail | null) => {
      if (batch != null) {
        setFeedbackBatch(batch);
        if (batch.application?.stage) {
          setApplicationStage(batch.application.stage as ApplicationStage);
        }
      } else if (applicationId && formId) {
        try {
          const next = await getUserActiveFeedbackBatch(applicationId, formId);
          setFeedbackBatch(next);
          if (!next) setActiveFeedbackItem(null);
          if (next?.application?.stage) {
            setApplicationStage(next.application.stage as ApplicationStage);
          }
        } catch {
          setFeedbackBatch(null);
          setActiveFeedbackItem(null);
        }
      } else {
        setFeedbackBatch(null);
        setActiveFeedbackItem(null);
      }
      if (applicationId) {
        try {
          const app = await getApplication(applicationId);
          setApplicationStage(app.stage);
        } catch {
          /* keep prior stage */
        }
      }
    },
    [applicationId, formId]
  );

  /**
   * Save the current section's answers without navigating.
   * Called by FeedbackTab before resolve / resubmit actions.
   * Uses a ref wrapper so FeedbackTab always calls the latest version.
   */
  const saveDraftImplRef = useRef({
    formId,
    applicationId,
    activeCategory,
    formData,
    formatForSubmission,
  });
  saveDraftImplRef.current = { formId, applicationId, activeCategory, formData, formatForSubmission };

  const saveDraft = useCallback(async (): Promise<void> => {
    const { formId: fId, applicationId: appId, activeCategory: cat, formData: data, formatForSubmission: fmt } =
      saveDraftImplRef.current;
    if (!fId || !appId) return;
    // Client worklist #81: a hidden count-driven pair is saved empty, so
    // answering "No" to children cannot leave old children on the N-400.
    const categoryAnswers = clearHiddenCountDrivenAnswers(
      getCategoryAnswers(cat, data),
      data,
      formQuestionsRef.current,
    );
    if (Object.keys(categoryAnswers).length === 0) return;
    const formattedAnswers = fmt(categoryAnswers);
    await submitFormAnswers(appId, fId, formattedAnswers);
    lastSavedFormDataRef.current = {
      ...lastSavedFormDataRef.current,
      ...categoryAnswers,
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load saved answers when form and application are available
  useEffect(() => {
    const loadSavedAnswers = async () => {
      if (!formId || !applicationId || !formStructure) return;

      try {
        const savedAnswers = await getFormAnswers(applicationId, formId);

        // Transform backend format to frontend format
        // Backend returns: { questionId: answer } or { questionId: [answer1, answer2, ...] }
        // Frontend uses: question.name || `q_${question.id}` as the key
        const transformedAnswers: Record<string, any> = {};

        const allQuestions = [
          ...collectQuestionsInCategories(
            formStructure.categoryTree?.length
              ? formStructure.categoryTree
              : formStructure.categories,
          ),
          ...(formStructure.uncategorizedQuestions ?? []),
        ];

        // Build a map of questionId -> question for quick lookup
        const questionMap = new Map<number, any>();
        allQuestions.forEach((q) => questionMap.set(q.id, q));

        Object.entries(savedAnswers).forEach(([questionIdStr, value]) => {
          const questionId = parseInt(questionIdStr, 10);
          const question = questionMap.get(questionId);

          // Determine the input name (question.name or q_${questionId})
          const inputName = question?.name || `q_${questionId}`;

          // For option-bearing checkbox questions the renderer expects an
          // array of selected option values. The backend's getFormAnswers
          // merge only promotes to an array when there are >= 2 rows, so
          // a single-selection answer would arrive as a scalar and the
          // renderer would treat it as "no selection". Normalise here so
          // the rest of the code has a stable invariant.
          const isCheckboxWithOptions =
            (question?.answerType === "checkbox" ||
              question?.answerType === "checkbox_group" ||
              question?.type === "checkbox" ||
              question?.type === "checkbox_group") &&
            Array.isArray(question?.options) &&
            (question?.options?.length ?? 0) > 0;

          const normalisedValue = isCheckboxWithOptions
            ? Array.isArray(value)
              ? value
              : value != null && value !== ""
                ? [value]
                : []
            : value;

          // Backend already returns arrays for multiple answers, so we can use value directly
          transformedAnswers[inputName] = normalisedValue;

          // Also set by questionId for compatibility
          transformedAnswers[questionIdStr] = normalisedValue;
        });

        const prefillQuestions = allQuestions.filter(
          (q) =>
            Array.isArray(q.validationRules?.prefillFrom) &&
            q.validationRules.prefillFrom.length > 0,
        );
        const sourceAnswersByKey = new Map<string, Record<string, any>>();

        if (prefillQuestions.length > 0) {
          const applicationForms = await getApplicationForms(applicationId);

          for (const question of prefillQuestions) {
            const mappings = question.validationRules?.prefillFrom ?? [];
            for (const mapping of mappings) {
              const key = sourceKey(mapping);
              if (sourceAnswersByKey.has(key)) continue;

              let sourceFormId = mapping.formId;
              if (!sourceFormId && mapping.formNumber) {
                sourceFormId = applicationForms.flatForms?.find(
                  (applicationForm) =>
                    applicationForm.form?.formNumber === mapping.formNumber,
                )?.form?.id;
              }
              if (!sourceFormId) sourceFormId = formId;
              if (!sourceFormId) continue;

              sourceAnswersByKey.set(
                key,
                sourceFormId === formId
                  ? savedAnswers
                  : await getFormAnswers(applicationId, sourceFormId),
              );
            }
          }
        }

        // Set the loaded answers into formData and keep the initial snapshot
        // aligned so Back only warns after the user edits this section.
        setFormData((prev) => {
          const next = {
            ...prev,
            ...transformedAnswers,
          };
          applyPrefillMappings(next, allQuestions, sourceAnswersByKey);
          lastSavedFormDataRef.current = next;
          return next;
        });
      } catch (error: any) {
        // If answers don't exist yet (404), that's fine - form starts empty
        if (error?.response?.status !== 404) {
          console.error("Error loading saved answers:", error);
        }
      } finally {
        setSavedAnswersHydrated(true);
      }
    };

    loadSavedAnswers();
  }, [formId, applicationId, formStructure, setFormData]);

  const formQuestions = useMemo(() => {
    if (!formStructure) return [] as QuestionItem[];
    return [
      ...collectQuestionsInCategories(
        formStructure.categoryTree?.length
          ? formStructure.categoryTree
          : formStructure.categories,
      ),
      ...(formStructure.uncategorizedQuestions ?? []),
    ];
  }, [formStructure]);

  formQuestionsRef.current = formQuestions;

  // Which questions are visible across the whole form, following dependencies
  // through (client worklist #84). Computed once per change of answers and
  // handed to every caller, so the sidebar, the page, Save & Next and the
  // validators all agree — and agree with the server.
  const visibleIds = useMemo(
    () => visibleQuestionIds(formQuestions, formData),
    [formQuestions, formData],
  );

  const entryCountEffectKey = useMemo(
    () => buildInitializeEntryCountEffectKey(formData, formQuestions),
    [formQuestions, formData],
  );

  // Task 15: pad multi-entry composites from a reported count (grow-only).
  useEffect(() => {
    if (!savedAnswersHydrated || !formQuestions.length) return;
    setFormData((prev) => {
      const next = { ...prev };
      const changed = applyInitializeEntryCounts(next, formQuestions);
      return changed ? next : prev;
    });
  }, [savedAnswersHydrated, formQuestions, entryCountEffectKey, setFormData]);

  // After saved answers hydrate, ensure the open section is still visible
  // (conditional rules may hide the structurally-first section).
  useEffect(() => {
    if (!savedAnswersHydrated || !formStructure || !activeCategory) return;
    if (postHydrationCategorySyncDoneRef.current) return;

    const roots =
      formStructure.categoryTree?.length > 0
        ? formStructure.categoryTree
        : formStructure.categories ?? [];
    if (roots.length === 0) return;

    postHydrationCategorySyncDoneRef.current = true;
    const visibleLeaves = getVisibleLeafCategories(roots, formData, visibleIds);
    if (visibleLeaves.length === 0) return;

    const isActiveVisible = visibleLeaves.some(
      (category) => category.id === activeCategory.id
    );
    if (!isActiveVisible) {
      selectCategoryById(visibleLeaves[0].id);
    }
  }, [
    savedAnswersHydrated,
    formStructure,
    formData,
    activeCategory,
    selectCategoryById,
  ]);

  useEffect(() => {
    if (!applicationId) return;
    getApplication(applicationId)
      .then((app) => setApplicationStage(app.stage))
      .catch(() => setApplicationStage(null));
  }, [applicationId]);

  useEffect(() => {
    if (!applicationId || !formId) return;
    setFeedbackLoading(true);
    getUserActiveFeedbackBatch(applicationId, formId)
      .then((batch) => {
        setFeedbackBatch(batch);
        if (batch?.application?.stage) {
          setApplicationStage(batch.application.stage as ApplicationStage);
        }
      })
      .catch(() => setFeedbackBatch(null))
      .finally(() => setFeedbackLoading(false));
  }, [applicationId, formId]);

  useEffect(() => {
    if (searchParams.get("openFeedback") === "1") {
      setDrawerOpen(true);
      if (!feedbackLoading) {
        setDrawerTab(
          (feedbackBatch?.items?.length ?? 0) > 0 ? "feedback" : "hints"
        );
      }
    }
  }, [searchParams, feedbackLoading, feedbackBatch]);

  useEffect(() => {
    if (!showFeedbackTab && drawerTab === "feedback") {
      setDrawerTab("hints");
    }
  }, [showFeedbackTab, drawerTab]);

  useEffect(() => {
    const openFeedback = searchParams.get("openFeedback") === "1";
    if (!openFeedback) {
      openFeedbackInitialPickDoneRef.current = false;
      return;
    }
    if (!feedbackBatch?.items?.length) return;
    if (openFeedbackInitialPickDoneRef.current) return;
    if (activeCategory == null && isLoading) return;

    const id = pickDefaultFeedbackItemId(feedbackBatch, activeCategory, searchParams);
    if (id != null) {
      setSelectedFeedbackItemId(id);
    }
    const deepLinkQ = parsePositiveIntParam(searchParams.get("feedbackQuestionId"));
    if (deepLinkQ != null) {
      scrollFormToFeedbackQuestion(deepLinkQ);
    }
    openFeedbackInitialPickDoneRef.current = true;
  }, [searchParams, feedbackBatch, activeCategory, isLoading, scrollFormToFeedbackQuestion]);

  /**
   * Helper to parse validation errors from backend
   */
  const parseValidationErrors = (
    errors: Array<{ questionId: number; message: string }>,
  ) => {
    const errorMap: Record<string, string> = {};
    errors.forEach((err) => {
      // Map to both question ID and name format (q_{id})
      errorMap[err.questionId] = err.message;
      errorMap[`q_${err.questionId}`] = err.message;
    });
    return errorMap;
  };

  /**
   * Helper to filter answers by active category
   */
  const getCategoryAnswers = (
    category: Category | null,
    formData: Record<string, any>,
  ) => {
    if (!category || !category.questions) return {};

    const filtered: Record<string, any> = {};

    // Iterate over questions in the category to ensure ALL keys are present
    // This forces backend validation for required fields even if they are empty/untouched
    category.questions.forEach((question) => {
      const inputName = question.name || `q_${question.id}`;
      // Get value from formData
      const value = formData[inputName];

      // If value exists, use it. If undefined, set as empty string (or empty array if multiple)
      // to ensure the key exists in payload.
      if (value !== undefined) {
        filtered[inputName] = value;
      } else {
        // Default based on type could be better, but empty string triggers required check
        filtered[inputName] = "";
      }
    });

    return filtered;
  };

  /**
   * Client-side pre-validation for required fields in active category
   */
  const validateCategory = (
    category: Category | null,
    formData: Record<string, any>,
  ) => {
    if (!category) return { isValid: true, errors: {} };

    const errors: Record<string, string> = {};
    category.questions?.forEach((question) => {
      if (!question.isRequired) return;

      const inputName = question.name || `q_${question.id}`;
      const value = formData[inputName];
      const atype = (question.answerType || question.type || "").toLowerCase();

      if (atype === "weight") {
        const o = value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
        const d1 = String(o.pounds_1 ?? "").trim();
        const d2 = String(o.pounds_2 ?? "").trim();
        const d3 = String(o.pounds_3 ?? "").trim();
        if (!d1 || !d2 || !d3) {
          errors[inputName] =
            "Enter all three digits of your weight in pounds (use 0 in the first box if needed).";
        }
        return;
      }

      if (
        !value ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === "string" && value.trim() === "")
      ) {
        errors[inputName] = "This field is required";
      }
    });

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  };

  const getFormCategoryRoots = (): Category[] => {
    if (!formStructure) return [];
    return formStructure.categoryTree?.length > 0
      ? formStructure.categoryTree
      : formStructure.categories ?? [];
  };

  const getVisibleLeafCategoryList = (): Category[] => {
    if (!formStructure) return [];
    return getVisibleLeafCategories(getFormCategoryRoots(), formData, visibleIds);
  };

  const getActiveVisibleCategoryIndex = (): number => {
    if (!activeCategory) return -1;
    return getVisibleLeafCategoryList().findIndex(
      (category) => category.id === activeCategory.id
    );
  };

  /**
   * Navigate to the next section that has visible questions (skips
   * conditionally hidden sections — same order as the sidebar).
   */
  const navigateToNextCategory = () => {
    if (!formStructure || !activeCategory) return;

    const visibleLeaves = getVisibleLeafCategoryList();
    const currentIndex = getActiveVisibleCategoryIndex();
    if (currentIndex < 0 || currentIndex >= visibleLeaves.length - 1) return;

    selectCategoryById(visibleLeaves[currentIndex + 1].id);
  };

  const navigateToPreviousCategory = () => {
    if (!formStructure || !activeCategory) return;

    const visibleLeaves = getVisibleLeafCategoryList();
    const currentIndex = getActiveVisibleCategoryIndex();
    if (currentIndex <= 0) return;

    selectCategoryById(visibleLeaves[currentIndex - 1].id);
  };

  const canNavigateToPreviousCategory = (): boolean =>
    getActiveVisibleCategoryIndex() > 0;

  /** True when the active section is the last one with visible questions. */
  const isLastCategory = (): boolean => {
    if (!formStructure || !activeCategory) return false;

    const visibleLeaves = getVisibleLeafCategoryList();
    if (visibleLeaves.length === 0) return false;
    return (
      activeCategory.id === visibleLeaves[visibleLeaves.length - 1].id
    );
  };

  const activeCategoryHasUnsavedChanges = (): boolean => {
    const currentAnswers = getCategoryAnswers(activeCategory, formData);
    const savedAnswers = getCategoryAnswers(
      activeCategory,
      lastSavedFormDataRef.current
    );
    return !answersEqual(currentAnswers, savedAnswers);
  };

  const handleBack = async () => {
    if (isSaving) return;
    if (!canNavigateToPreviousCategory()) return;

    if (activeCategoryHasUnsavedChanges()) {
      const ok = await confirm({
        title: "Leave without saving?",
        message:
          "You have unsaved changes in this section. Go back without saving them first?",
        confirmLabel: "Go back",
        cancelLabel: "Stay",
        destructive: true,
      });
      if (!ok) return;
    }

    navigateToPreviousCategory();
  };

  const handleSubmit = async () => {
    if (isFormViewOnly) return;
    if (!formId || !applicationId) {
      toast.error("Missing form ID or application ID");
      return;
    }

    // 1. Client-side pre-validation for this section. Same semantic as the
    //    backend submit gate: every visible non-`info` question must have a
    //    non-empty answer. We compute against the live in-memory formData
    //    (not the saved state) so the user gets immediate feedback on what
    //    they just typed.
    const sectionMisses = findMissingInCategory(activeCategory, formData, visibleIds);
    const formatMisses = findFormatErrorsInCategory(activeCategory, formData, visibleIds);

    // Invalid format (e.g. us_ssn) — block save; don't persist bad values.
    if (formatMisses.length > 0) {
      const errors: Record<string, string> = {};
      for (const miss of formatMisses) {
        errors[miss.inputName] = miss.message;
        errors[String(miss.question.id)] = miss.message;
        errors[`q_${miss.question.id}`] = miss.message;
      }
      setValidationErrors(errors);
      dispatchFormScrollToQuestion({
        questionId: formatMisses[0].question.id,
        highlight: true,
      });
      return;
    }

    // 2. Filter answers by active category. We always save what the user
    //    HAS typed even when validation fails — losing typed work because
    //    a required field is blank would be hostile.
    // Client worklist #81: see clearHiddenCountDrivenAnswers.
    const categoryAnswers = clearHiddenCountDrivenAnswers(
      getCategoryAnswers(activeCategory, formData),
      formData,
      formQuestions,
    );
    const hasAnswersToSave = Object.keys(categoryAnswers).length > 0;

    // 3. If nothing to save AND nothing missing, the section is genuinely
    //    empty (e.g. an introductory page). On non-last sections, just
    //    advance; on the LAST section, fall through to the submit modal
    //    so the user can still complete submission even if their last
    //    visit didn't touch any field.
    if (!hasAnswersToSave && sectionMisses.length === 0) {
      if (isLastCategory()) {
        await openSubmitModal();
        return;
      }
      navigateToNextCategory();
      return;
    }

    // 4. Persist whatever the user has typed (best-effort) before deciding
    //    whether to navigate. We still set a fresh validationErrors map so
    //    stale errors from a previous click clear.
    setValidationErrors({});
    let saveFailedWithBackendValidation = false;

    if (hasAnswersToSave) {
      try {
        setIsSaving(true);
        const formattedAnswers = formatForSubmission(categoryAnswers);
        await submitFormAnswers(applicationId, formId, formattedAnswers);
        lastSavedFormDataRef.current = {
          ...lastSavedFormDataRef.current,
          ...categoryAnswers,
        };
        void refreshReadiness();
      } catch (error: any) {
        console.error("Error saving form:", error);
        if (error.validationErrors) {
          const backendErrors: Array<{ questionId: number; message: string }> =
            error.validationErrors;
          setValidationErrors(parseValidationErrors(backendErrors));
          saveFailedWithBackendValidation = true;

          // Take the applicant TO the problem, as the client-side branches
          // above do. This used to scroll to the top of the page, away from
          // the question the message renders under — on a long question such
          // as the address history nothing visibly changed, and Save & Next
          // looked like it had silently done nothing.
          const inThisSection = (e: { questionId: number }) =>
            Boolean(
              activeCategory?.questions?.some(
                (q: any) => Number(q.id) === Number(e.questionId),
              ),
            );
          const shownHere = backendErrors.find(inThisSection);
          if (shownHere) {
            dispatchFormScrollToQuestion({
              questionId: shownHere.questionId,
              highlight: true,
            });
          }
          // An error for a question outside this section has nowhere to
          // render inline, so say it out loud rather than show nothing.
          const notShownHere = backendErrors.find((e) => !inThisSection(e));
          if (notShownHere) {
            toast.error(
              notShownHere.message ||
                "Some answers need attention before you can continue.",
            );
          }
        } else {
          toast.error(`Failed to save form: ${error?.message || "Unknown error"}`);
          return; // hard failure on save — don't advance, don't overwrite errors
        }
      } finally {
        setIsSaving(false);
      }
    }

    // 5. After saving (or if there was nothing to save), enforce
    //    per-section validation. If anything is missing, surface inline
    //    errors and scroll the first one into view — but DON'T navigate.
    if (sectionMisses.length > 0) {
      const errors: Record<string, string> = {};
      for (const miss of sectionMisses) {
        errors[miss.inputName] = "This question requires an answer.";
        // Also key by id and `q_${id}` so QuestionRenderer's multi-key
        // lookup finds the error regardless of which key formData uses.
        errors[String(miss.question.id)] = "This question requires an answer.";
        errors[`q_${miss.question.id}`] = "This question requires an answer.";
      }
      setValidationErrors(errors);
      // No toast — inline per-field errors + the scroll-to-first-miss
      // highlight give the user enough feedback without an extra interrupt.
      dispatchFormScrollToQuestion({
        questionId: sectionMisses[0].question.id,
        highlight: true,
      });
      return;
    }

    // 6. Backend rejected the save with its own validation errors. Stay
    //    put so the user can fix them.
    if (saveFailedWithBackendValidation) return;

    // 7. Last section: open the submit confirmation modal instead of
    //    silently no-op'ing the navigation. Re-checks readiness with the
    //    freshly-saved answers so the modal opens in the correct state
    //    ('confirm' when ready, 'missing' with the gap list otherwise).
    if (isLastCategory()) {
      await openSubmitModal();
      return;
    }

    // 8. Not the last section — advance.
    navigateToNextCategory();
  };

  /**
   * Compute the modal state from the latest readiness and open it.
   * Called from handleSubmit only when the user has just saved the
   * LAST section, so this is the explicit "trying to submit" gesture.
   */
  const openSubmitModal = async () => {
    if (!applicationId || !formId) return;
    let latest = readiness;
    try {
      setIsCheckingReadiness(true);
      latest = await getFormReadiness(applicationId, formId);
      setReadiness(latest);
    } catch (error) {
      console.error("Failed to check readiness for submit modal:", error);
      toast.error("Couldn't verify form readiness. Please try again.");
      return;
    } finally {
      setIsCheckingReadiness(false);
    }
    if (latest?.isReadyToSubmit) {
      setSubmitModalState("confirm");
      setSubmitModalMissingCategories([]);
    } else {
      setSubmitModalState("missing");
      // Aggregate the flat missing-question list into a pruned tree of
      // category names so the modal can show "which sections need
      // attention" instead of dumping every individual question label.
      // Part / item numbers stay in the underlying data for PDF mapping.
      setSubmitModalMissingCategories(
        buildMissingCategoriesTree(
          latest?.missing ?? [],
          formStructure?.categoryTree,
        ),
      );
    }
    setSubmitModalOpen(true);
  };

  /**
   * Fetch submission readiness from the backend. Cheap (~50ms on a 296-q
   * form) so we call it on mount and after every successful save instead of
   * trying to mirror the gate logic on the client. Errors are non-fatal —
   * the bar simply stays disabled with a "checking" hint.
   */
  const refreshReadiness = useCallback(async () => {
    if (!applicationId || !formId) return;
    try {
      setIsCheckingReadiness(true);
      const r = await getFormReadiness(applicationId, formId);
      setReadiness(r);
    } catch (error) {
      console.error("Failed to fetch form readiness:", error);
    } finally {
      setIsCheckingReadiness(false);
    }
  }, [applicationId, formId]);

  // Initial readiness load once we know which application/form we're on.
  useEffect(() => {
    refreshReadiness();
  }, [refreshReadiness]);

  /**
   * Mark form complete — only invoked after the user explicitly confirms in
   * the SubmitConfirmationModal. Saving and readiness checks were
   * already performed by `handleSubmit` before the modal opened, so
   * this is purely the completeForm + redirect step.
   */
  const handleConfirmSubmit = async () => {
    if (isFormViewOnly) return;
    if (!formId || !applicationId) {
      toast.error("Missing form or application ID");
      return;
    }
    try {
      setIsSubmitting(true);
      await completeForm(applicationId, formId);
      toast.success("Form marked complete.");
      setSubmitModalOpen(false);
      navigate(`/visa-application?applicationId=${applicationId}`);
    } catch (error: any) {
      console.error("Error submitting form:", error);
      toast.error(`Failed to submit: ${error?.message || "Unknown error"}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Navigate to a category that still has unanswered questions, from
   * the SubmitConfirmationModal's "missing" list. Closes the modal so
   * the user actually sees the destination, and scrolls to the first
   * missing question in that category if one is known.
   */
  const handleMissingCategoryClick = (
    categoryId: number,
    firstMissingQuestionId: number | null,
  ) => {
    let needsCategorySwitch = false;
    if (formStructure?.categoryTree) {
      const findCategory = (cats: Category[]): Category | null => {
        for (const c of cats) {
          if (c.id === categoryId) return c;
          if (c.subcategories) {
            const found = findCategory(c.subcategories);
            if (found) return found;
          }
        }
        return null;
      };
      const target = findCategory(formStructure.categoryTree);
      if (target && target.id !== activeCategory?.id) {
        handleSelectCategory(target);
        needsCategorySwitch = true;
      }
    }
    setSubmitModalOpen(false);
    // After a category switch the new questions need a frame to mount
    // before the scroll target exists; same-category clicks scroll
    // immediately. If we don't have a specific question to jump to
    // (only the category), just opening the section is enough.
    if (firstMissingQuestionId != null) {
      dispatchFormScrollToQuestion({
        questionId: firstMissingQuestionId,
        highlight: true,
        delayMs: needsCategorySwitch ? 80 : 0,
      });
    }
  };

  if (isLoading) {
    const loadingOverviewHref = applicationId
      ? `/visa-application?applicationId=${applicationId}`
      : "/dashboard";
    return (
      <div className="page_main page_main-after-login mt-[100px] py-[40px] bg-[#F7F8FA]">
        <div className="custom-container mx-auto">
          {/* Back link shown during loading too so the user can always
              bail out (cold form-definition loads can take a few seconds). */}
          <Link
            to={loadingOverviewHref}
            className="font-bold flex items-center text-[#1B365D] mb-6 hover:opacity-80 transition-opacity"
          >
            <img src={backArrow} alt="" aria-hidden className="me-2" />
            Back to Application Overview
          </Link>
          <div className="flex justify-between gap-4 items-start">
          {/* Sidebar skeleton */}
          <div className="hidden md:block w-[25%] shrink-0 bg-white shadow-[0px_1px_2px_0px_#0000000D] p-6 rounded-lg sticky top-[40px] self-start md:max-h-[80vh]">
            <div className="h-8 w-3/4 animate-pulse rounded bg-slate-200 mb-6" />
            <div className="space-y-2">
              <div className="h-11 w-full animate-pulse rounded-lg bg-slate-200" />
              <div className="h-11 w-11/12 animate-pulse rounded-lg bg-slate-200" />
              <div className="h-11 w-full animate-pulse rounded-lg bg-slate-200" />
              <div className="h-11 w-10/12 animate-pulse rounded-lg bg-slate-200" />
              <div className="h-11 w-full animate-pulse rounded-lg bg-slate-200" />
              <div className="h-11 w-11/12 animate-pulse rounded-lg bg-slate-200" />
              <div className="h-11 w-10/12 animate-pulse rounded-lg bg-slate-200" />
              <div className="h-11 w-full animate-pulse rounded-lg bg-slate-200" />
            </div>
          </div>

          {/* Form content skeleton */}
          <div className="relative flex-1 min-w-0">
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm space-y-4 md:min-h-[calc(80vh-120px)]">
              <div className="h-7 w-1/2 animate-pulse rounded bg-slate-200" />
              <div className="h-5 w-1/3 animate-pulse rounded bg-slate-200" />
              <div className="space-y-3 pt-2">
                <div className="h-4 w-3/4 animate-pulse rounded bg-slate-200" />
                <div className="h-11 w-full animate-pulse rounded-xl bg-slate-200" />
                <div className="h-4 w-2/3 animate-pulse rounded bg-slate-200" />
                <div className="h-11 w-full animate-pulse rounded-xl bg-slate-200" />
                <div className="h-4 w-1/2 animate-pulse rounded bg-slate-200" />
                <div className="h-11 w-full animate-pulse rounded-xl bg-slate-200" />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <div className="h-10 w-28 animate-pulse rounded-full bg-slate-200" />
                <div className="h-10 w-36 animate-pulse rounded-full bg-slate-200" />
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>
    );
  }

  /**
   * Where the in-page Back link should point. Prefer the application
   * overview tied to this form; fall back to /dashboard so the link is
   * never broken if the URL is missing its applicationId param.
   */
  const overviewHref = applicationId
    ? `/visa-application?applicationId=${applicationId}`
    : "/dashboard";

  return (
    <div className="page_main page_main-after-login mt-[100px] py-[40px] bg-[#F7F8FA]">
      <div className="custom-container mx-auto">
        {/*
         * Back link mirrors the one on the application overview
         * (VisaApplication/index.tsx). It sits above the form layout so
         * the user always has an explicit way out of the stepper without
         * relying on browser back, which can lose typed-but-unsaved
         * answers — they're persisted only on Save & Next.
         */}
        <Link
          to={overviewHref}
          className="font-bold flex items-center text-[#1B365D] mb-6 hover:opacity-80 transition-opacity"
        >
          <img src={backArrow} alt="" aria-hidden className="me-2" />
          Back to Application Overview
        </Link>
        <div className="flex justify-between gap-4 items-start">
          {/* SIDEBAR */}
          <FormSidebar
          categories={formStructure?.categoryTree || []}
          activeCategoryId={activeCategory?.id || null}
          expandedCategories={expandedCategories}
          onToggleExpand={handleToggleExpand}
          onSelectCategory={handleSelectCategory}
          title={formTitle}
          formData={formData}
          visibleIds={visibleIds}
        />

        {/* Form + hints/feedback: form uses all space left of the drawer when open; full width when drawer closed */}
        <div className="relative flex-1 min-w-0 flex flex-col md:flex-row items-stretch md:items-start gap-4 transition-all duration-300">
          <div className="min-w-0 flex-1">
            <FormContent
              activeCategory={activeCategory}
              formData={formData}
              visibleIds={visibleIds}
              // Applicant edits only (#82/#83): field renderers that write the
              // form state directly still go through the count reconcile.
              setFormData={setFormDataFromEdit}
              handleChange={handleChange}
              handleMultipleEntryChange={handleMultipleEntryChange}
              handleAddEntry={handleAddEntry}
              handleRemoveEntry={handleRemoveEntry}
              handleCompositeFieldChange={handleCompositeFieldChange}
              handleMultipleCompositeChange={handleMultipleCompositeChange}
              isLoading={isLoading}
              loadError={loadError}
              handleSubmit={handleSubmit}
              handleBack={handleBack}
              isSaving={isSaving}
              applicationId={applicationId}
              formId={formId}
              validationErrors={validationErrors}
              isLastCategory={isLastCategory()}
              onOpenHintsWithHint={openHintsWithHint}
              questionIdsWithFeedback={questionIdsWithFeedback}
              onOpenFeedbackWithQuestion={openFeedbackWithQuestion}
              restrictEditsToFeedbackFields={restrictEditsToFeedbackFields}
              questionIdsWithPendingFeedback={questionIdsWithPendingFeedback}
              isFormViewOnly={isFormViewOnly}
              activeFeedbackItem={activeFeedbackItem}
              canGoBack={canNavigateToPreviousCategory()}
            />
          </div>

          {/* EMBEDDED DRAWER */}
          <FormDrawer
            drawerOpen={drawerOpen}
            activeCategory={activeCategory}
            formStructure={formStructure}
            formData={formData}
            activeHint={activeHint}
            onClose={() => setDrawerOpen(false)}
            drawerTab={drawerTab}
            onTabChange={setDrawerTab}
            applicationId={applicationId}
            formId={formId}
            feedbackBatch={feedbackBatch}
            feedbackLoading={feedbackLoading}
            onFeedbackBatchUpdated={handleFeedbackBatchUpdated}
            onFeedbackNavigateToSource={handleFeedbackNavigateToSource}
            highlightedFeedbackItemId={selectedFeedbackItemId}
            hasActionRequiredFeedback={hasActionRequiredFeedback}
            selectCategoryById={selectCategoryById}
            navigateToCategoryContainingQuestion={
              navigateToCategoryContainingQuestion
            }
            onEnsureHintsOpen={ensureHintsDrawerOpen}
            showFeedbackTab={showFeedbackTab}
            onSaveBeforeFeedbackAction={saveDraft}
            onActiveFeedbackItemChange={setActiveFeedbackItem}
          />
        </div>
        </div>
      </div>

      {confirmDialogProps ? <ConfirmDialog {...confirmDialogProps} /> : null}

      {/* Submission confirmation modal — opens only when the user hits
          Save on the LAST section. Two states: 'confirm' (form is ready,
          ask for go/no-go) and 'missing' (form has gaps; show them with a
          jump-to-question list). The form stays editable until the user
          clicks "Submit for review" inside this modal. */}
      <SubmitConfirmationModal
        open={submitModalOpen}
        state={submitModalState}
        missingCategories={submitModalMissingCategories}
        isSubmitting={isSubmitting}
        onClose={() => {
          if (!isSubmitting) setSubmitModalOpen(false);
        }}
        onConfirm={handleConfirmSubmit}
        onMissingCategoryClick={handleMissingCategoryClick}
      />
    </div>
  );
};

export default StepperForm;
